<template>
  <div>
    <div v-show="isLoggedIn" class="mb-4">
      <div class="pt-2 pb-3">
        You are signed in as {{ user | displayName }}. Your name will appear
        with any {{ inputType.toLowerCase() }} you post.
      </div>
      <CommentModerationNotification v-if="!isReply" />
      <v-textarea
        id="loggedInUser"
        v-model="model"
        :error-messages="maxLengthError()"
        :placeholder="`Add your ${inputType.toLowerCase()}`"
        :aria-describedby="ariaDescribedByID"
        outlined
        auto-grow
        rows="3"
        :aria-label="`${inputType} input`"
        @input="updateMaxLengthSRMessage"
      >
      </v-textarea>

      <div
        v-if="!maxWordsLimitError"
        aria-hidden="true"
        class="font-small ml-3 mt-n6 mb-3"
      >
        {{ maxLengthMessage }}
      </div>
      <span :id="staticSRHint" class="d-sr-only">
        {{ limitSRHintMessage }}
      </span>
      <span role="status" aria-atomic="true" class="d-sr-only">
        {{ maxLengthSRMessage }}</span
      >

      <v-btn
        class="white--text"
        :disabled="disablePostCommentButton"
        color="action"
        rounded
        data-test-id="PostCommentButton"
        @click="onPostComment"
        >Post {{ inputType.toLowerCase() }}</v-btn
      >
    </div>
    <div v-show="!isLoggedIn" class="py-2 px-4">
      <button
        id="notLoggedInUser"
        class="underline"
        role="link"
        tabindex="0"
        data-test-id="LogInToCommentLink"
        @click="login"
        @keyup.enter="login"
      >
        <span
          >Log in or register with
          {{ supportOrcidLogin ? "ORCID" : theme.accountName }} to comment</span
        >
      </button>
      <CommentModerationNotification />
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import mapFlags from "~/plugins/utilities/mapFlags";

import {
  characterCount,
  wordCount,
  maxLengthMessage,
  maxLengthError,
  limitSRHintMessage,
} from "~/components/forms/utilities/modelMaxLength";
import isLoggedIn from "~/plugins/utilities/isLoggedIn";
import CommentModerationNotification from "~/components/comments/components/CommentModerationNotification.vue";
import debounce from "~/store/manage/utilities/debounce";

export default {
  name: `AddComment`,
  components: { CommentModerationNotification },
  props: {
    maxWords: { type: Number, default: 300 },
    isReply: { type: Boolean, default: false },
  },
  data() {
    return {
      model: "",
      maxLengthSRMessage: undefined,
    };
  },
  computed: {
    ...mapState({
      user: (state) => state.user,
      theme: (state) => state.theme,
    }),
    ...mapFlags(["supportOrcidLogin"]),
    isLoggedIn() {
      return isLoggedIn(this.user);
    },
    disablePostCommentButton() {
      return Boolean(!this.model || this.maxLengthError());
    },
    inputType() {
      return this.isReply ? "Reply" : "Comment";
    },
    maxWordsLimitError() {
      return this.wordCount > this.maxWords;
    },
    addCommentMessageId() {
      return "add-comment";
    },
    staticSRHint() {
      return "add-comment-static-hint";
    },
    addCommentErrorMessageId() {
      return "add-comment-error";
    },
    ariaDescribedByID() {
      return this.maxWordsLimitError
        ? `${this.addCommentErrorMessageId} ${this.staticSRHint}`
        : `${this.addCommentMessageId} ${this.staticSRHint}`;
    },
    characterCount,
    wordCount,
    maxLengthMessage,
    limitSRHintMessage,
  },
  created() {
    this.updateMaxLengthSRMessage = debounce(() => {
      this.maxLengthSRMessage = this.maxWordsLimitError
        ? this.maxLengthError()
        : this.maxLengthMessage;
    }, 500);
  },
  methods: {
    login({ currentTarget }) {
      this.$root.$emit("show-login-options", currentTarget);
    },
    onPostComment({ currentTarget }) {
      this.$root.$emit("show-confirmation", {
        title: `${this.inputType} confirmation`,
        instruction: `Please confirm you want to post this ${this.inputType.toLowerCase()}. You will not be able to edit or remove the ${this.inputType.toLowerCase()} after it has been posted.`,
        confirmationButtonText: "Post",
        action: () => {
          this.$emit("postComment", this.model);
          this.model = "";
        },
        currentTarget,
      });
    },
    maxLengthError,
  },
};
</script>
